import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Email } from '@kula-ai/components';
import type {
  EditorProps,
  EmailCompositionValue,
  EmailProps,
} from '@kula-ai/components';
import type { SelectOption } from '@kula-ai/components';
import {
  applyTemplateMetadataOnChange,
  mapTemplateAttachmentsToEditorItems,
} from '../InterviewActivity/communication/helpers/templateApply';
import type {
  InterviewEditorAttachmentItem,
  InterviewVariablesByCategory,
} from '../InterviewActivity/communication/communication.types';

function interviewFileToAttachmentItem(
  file: File
): InterviewEditorAttachmentItem {
  return {
    key: `${file.name}-${file.size}-${file.lastModified}`,
    name: file.name,
    size: file.size,
    mimeType: file.type,
    file,
  };
}

export type InterviewEmailEditorVariant = 'calendarInvite' | 'richEmail';

export type TemplatedEmailProps = Omit<
  EmailProps,
  'onChange' | 'templateOptions'
> & {
  templateOptions: SelectOption[];
  value: EmailCompositionValue;
  onChange: (_value: EmailCompositionValue) => void;
  editorVariant?: InterviewEmailEditorVariant;
  variables?: InterviewVariablesByCategory;
  attachments?: InterviewEditorAttachmentItem[];
  onAttachmentsChange?: (_items: InterviewEditorAttachmentItem[]) => void;
  uploadInlineImage?: EditorProps['uploadInlineImage'];
};

export function TemplatedEmail({
  templateOptions,
  value,
  onChange,
  editorProps,
  editorVariant = 'calendarInvite',
  variables,
  attachments,
  onAttachmentsChange,
  uploadInlineImage,
  subjectVariableInputProps,
  ...rest
}: TemplatedEmailProps) {
  const prev = useRef(value);
  useEffect(() => {
    prev.current = value;
  }, [value]);

  const handleChange = useCallback(
    (next: EmailCompositionValue) => {
      const merged = applyTemplateMetadataOnChange(
        prev.current,
        next,
        templateOptions
      );
      prev.current = merged;
      onChange(merged);
    },
    [onChange, templateOptions]
  );

  // When template changes, seed its attachments into user-controlled state
  const prevTemplateId = useRef(value.template);
  useEffect(() => {
    if (value.template === prevTemplateId.current) return;
    prevTemplateId.current = value.template;

    if (!onAttachmentsChange) return;
    if (!value.template) {
      // Template cleared — remove template-sourced attachments (keep user files)
      const userOnly = (attachments ?? []).filter((a) => a.file);
      onAttachmentsChange(userOnly);
      return;
    }
    // Template selected — seed its attachments
    const opt = templateOptions.find((o) => o.value === value.template);
    const templateItems = mapTemplateAttachmentsToEditorItems(
      opt?.data?.attachments
    ) as InterviewEditorAttachmentItem[];
    if (templateItems.length) {
      // Keep existing user files, add template attachments
      const userFiles = (attachments ?? []).filter((a) => a.file);
      onAttachmentsChange([...templateItems, ...userFiles]);
    }
  }, [value.template, templateOptions, attachments, onAttachmentsChange]);

  const toolbarFeatures = useMemo(
    () =>
      editorVariant === 'calendarInvite'
        ? { image: false, attachment: false }
        : { image: true, attachment: true },
    [editorVariant]
  );

  const mergedEditorProps = useMemo(() => {
    const allAttachments =
      editorVariant === 'richEmail' && attachments?.length
        ? attachments
        : undefined;

    const attachmentHandlers =
      editorVariant === 'richEmail' && onAttachmentsChange
        ? {
            onAttachFiles: (files: File[]) => {
              const added = files.map(interviewFileToAttachmentItem);
              onAttachmentsChange([...(attachments ?? []), ...added]);
            },
            onRemoveAttachment: (key: string) => {
              onAttachmentsChange(
                (attachments ?? []).filter((a) => a.key !== key)
              );
            },
          }
        : {};

    const inlineImageProp =
      editorVariant === 'richEmail' && uploadInlineImage
        ? { uploadInlineImage }
        : {};

    return {
      ...editorProps,
      variables: variables ?? editorProps?.variables,
      toolbarFeatures: {
        ...editorProps?.toolbarFeatures,
        ...toolbarFeatures,
      },
      ...(allAttachments ? { attachments: allAttachments } : {}),
      ...attachmentHandlers,
      ...inlineImageProp,
    };
  }, [
    attachments,
    editorProps,
    editorVariant,
    onAttachmentsChange,
    toolbarFeatures,
    uploadInlineImage,
    variables,
  ]);

  const mergedSubjectVariableInputProps = useMemo(
    () =>
      variables && Object.keys(variables).length > 0
        ? { ...subjectVariableInputProps, variables }
        : subjectVariableInputProps,
    [subjectVariableInputProps, variables]
  );

  return (
    <Email
      {...rest}
      editorProps={mergedEditorProps}
      subjectVariableInputProps={mergedSubjectVariableInputProps}
      templateOptions={templateOptions}
      value={value}
      onChange={handleChange}
    />
  );
}
